import { useMemo } from 'react';
import Select from 'react-select';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import type { DirDimensionType } from '@econtractor/types';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import type { SelectOption } from '../types';

interface DimensionSelectProps {
    value?: number;
    onChange: (option: SelectOption | null) => void;
    required?: boolean;
    disabled?: boolean;
}

const DimensionSelect = ({ value, onChange, required = true, disabled = false }: DimensionSelectProps) => {
    const { t, i18n } = useTranslation(['Products']);
    const { auth } = useAuth();
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;
    const options = useMemo<SelectOption[]>(
        () =>
            localSettings?.DIRS?.Dimension?.map((dimension: DirDimensionType) => ({
                value: dimension.ID,
                label: tDirName(t, dimension.i18nKey, dimension.dimensionName),
            })) || [],
        [localSettings?.DIRS?.Dimension, t, i18n.language],
    );

    return (
        <Select<SelectOption>
            options={options}
            styles={{
                ...selectTheme,
                control: (base, state) => ({
                    ...base,
                    ...(selectTheme.control ? selectTheme.control(base, state) : {}),
                }),
            }}
            required={required}
            className="w-100"
            value={options?.find((dimension) => dimension?.value == value) || null}
            onChange={onChange}
            isMulti={false}
            isClearable={!required}
            isDisabled={disabled || !hasUserAccess(auth?.user, 'prodEdit')}
        />
    );
};

export default DimensionSelect;
